// SetVersionPage: one Set_Version, read-only (blueprint-sets Req 3.6, 2.8).
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import Alert from '@cloudscape-design/components/alert';
import Button from '@cloudscape-design/components/button';
import Container from '@cloudscape-design/components/container';
import FormField from '@cloudscape-design/components/form-field';
import Header from '@cloudscape-design/components/header';
import Select from '@cloudscape-design/components/select';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Spinner from '@cloudscape-design/components/spinner';
import Table from '@cloudscape-design/components/table';
import { api } from '../../api/client';
import { keys, useSet } from '../../api/hooks';
import { SummaryRulesEditor, type SummaryRulesMember } from './SummaryRulesEditor';
import { formatDate } from '../../lib/format';
import { errorMessage } from '../../lib/notify';
import { CoverageFloorInput, coverageFloorText } from './coverageFloor';
import { memberRefLabel, type SetVersion, SetVersionDiff, useMemberSchemas } from './setShared';
import type { SetDetail } from './SetDetailPage';

// ---------- SetVersionPage (Req 3.6, 2.8) ----------

export function SetVersionPage() {
  const { id = '', v = '' } = useParams();
  const n = Number(v);
  const valid = !!id && Number.isInteger(n) && n > 0;
  const set = useSet(id || undefined);
  const q = useQuery({ queryKey: keys.setVersion(id, n), queryFn: () => api.getSetVersion(id, n), enabled: valid });
  if (!valid) return <Alert type="error">Invalid version.</Alert>;
  if (q.error) return <Alert type="error">{errorMessage(q.error)}</Alert>;
  if (!q.data) return <Spinner size="large" />;
  return (
    <SetVersionView
      setId={id}
      setName={set.data?.set.name}
      versions={set.data?.versions ?? []}
      version={q.data.version}
    />
  );
}

function SetVersionView({
  setId,
  setName,
  versions,
  version,
}: {
  setId: string;
  setName: string | undefined;
  versions: SetDetail['versions'];
  version: SetVersion;
}) {
  const navigate = useNavigate();
  const [compareWith, setCompareWith] = useState<number | null>(null);
  const schemas = useMemberSchemas(version.members, undefined);
  const snapshot = new Map(version.memberSnapshot.map((m) => [m.blueprintId, m]));
  const rows = version.members.map((m, index) => ({
    ...m,
    index,
    ...(snapshot.get(m.blueprintId) ?? { name: 'Unknown blueprint', documentType: '', description: '' }),
  }));
  const rulesMembers: SummaryRulesMember[] = rows.map((r) => ({
    blueprintId: r.blueprintId,
    name: r.name,
    schema: schemas.schemaOf(r) ?? null,
  }));
  const others = versions
    .filter((x) => x.version !== version.version)
    .map((x) => ({ value: String(x.version), label: `Version ${x.version}` }));

  return (
    <SpaceBetween size="l">
      <Header
        variant="h1"
        description={`Published ${formatDate(version.publishedAt)} by ${version.publishedBy}${version.changeNote ? ` - ${version.changeNote}` : ''}`}
        actions={<Button onClick={() => navigate(`/sets/${setId}`)}>Back to set</Button>}
      >
        {`${setName ?? 'Blueprint set'} - Version ${version.version}`}
      </Header>
      <Table<(typeof rows)[number]>
        variant="container"
        header={
          <Header variant="h2" counter={`(${rows.length})`}>
            Members
          </Header>
        }
        items={rows}
        trackBy={(r) => `${r.index}:${r.blueprintId}`}
        columnDefinitions={[
          { id: 'name', header: 'Blueprint', cell: (r) => r.name, isRowHeader: true },
          { id: 'documentType', header: 'Document type', cell: (r) => r.documentType || '-' },
          { id: 'version', header: 'Version', cell: (r) => memberRefLabel(r.ref) },
          { id: 'description', header: 'Description', cell: (r) => r.description || '-' },
        ]}
      />
      <Container>
        <CoverageFloorInput value={coverageFloorText(version.coverageFloor)} readOnly />
      </Container>
      <Container header={<Header variant="h2">Packet summary rules</Header>}>
        <SummaryRulesEditor value={version.summaryRules} members={rulesMembers} onChange={() => undefined} readOnly />
      </Container>
      <Container header={<Header variant="h2">Compare</Header>}>
        <FormField label="Compare with" controlId="set-compare-with">
          <Select
            controlId="set-compare-with"
            placeholder={others.length === 0 ? 'No other versions' : 'Choose a version'}
            disabled={others.length === 0}
            options={others}
            selectedOption={others.find((o) => o.value === String(compareWith)) ?? null}
            onChange={(e) => setCompareWith(Number(e.detail.selectedOption.value))}
          />
        </FormField>
      </Container>
      {compareWith !== null && (
        <SetVersionDiff
          setId={setId}
          a={Math.min(compareWith, version.version)}
          b={Math.max(compareWith, version.version)}
        />
      )}
    </SpaceBetween>
  );
}
